/**
 * The toast host — one place a screen can say "that worked" without owning a stack of its own.
 *
 * Toasts here are receipts, not the record. A consequential action lands in the audit trail on the
 * entity; the toast just tells the operator the request was accepted. Errors stay until dismissed,
 * because an error that vanishes on a timer is an error nobody read.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { REPLAYED_EVENT } from '../lib/api/client';
import { Toast, type ToastTone } from '../components/feedback';

/** The standing receipt for an `Idempotency-Replayed` answer (doc 01 §5.4, doc 03 §6.8). */
export const REPLAYED_MESSAGE = 'This action had already been applied — nothing was done twice.';

export interface ToastRequest {
  tone?: ToastTone;
  message: string;
  action?: string;
  onAction?: () => void;
}

interface LiveToast extends ToastRequest {
  id: number;
}

export interface ToastApi {
  toast: (t: ToastRequest) => void;
  dismiss: (id: number) => void;
  /** Drops every standing error. The shell calls it on a route change: an error belongs to the
      screen whose request failed, and one left up reads as a fault in the screen opened next. */
  dismissErrors: () => void;
  toasts: readonly LiveToast[];
}

const ToastContext = createContext<ToastApi | null>(null);

/** Errors persist. Everything else clears itself. */
export const TOAST_TTL_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<LiveToast[]>([]);
  const nextId = useRef(1);
  /* Pending dismiss timers, cancelled on unmount: one left running fires a state update into a
     page that is gone (it surfaced as a post-teardown error in the admin gate). */
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const dismissErrors = useCallback(() => {
    setToasts((cur) => cur.filter((t) => t.tone !== 'error'));
  }, []);

  const toast = useCallback(
    (t: ToastRequest) => {
      const id = nextId.current++;
      setToasts((cur) => [...cur, { ...t, id }]);
      if (t.tone !== 'error') {
        const timer = setTimeout(() => {
          timers.current.delete(timer);
          dismiss(id);
        }, TOAST_TTL_MS);
        timers.current.add(timer);
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    const replayed = () => toast({ tone: 'info', message: REPLAYED_MESSAGE });
    window.addEventListener(REPLAYED_EVENT, replayed);
    return () => window.removeEventListener(REPLAYED_EVENT, replayed);
  }, [toast]);

  const api = useMemo(
    () => ({ toast, dismiss, dismissErrors, toasts }),
    [toast, dismiss, dismissErrors, toasts],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        style={{
          position: 'fixed',
          right: 'var(--sp-xxl)',
          bottom: 'var(--sp-xxl)',
          display: 'grid',
          gap: 'var(--sp-sm)',
          zIndex: 90,
          width: 380,
        }}
      >
        {toasts.map((t) => (
          <Toast
            key={t.id}
            tone={t.tone}
            message={t.message}
            action={t.action ?? 'Dismiss'}
            onAction={() => {
              t.onAction?.();
              dismiss(t.id);
            }}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  /* v8 ignore next -- the provider wraps the whole console; this is a wiring error, not a state. */
  if (!api) throw new Error('useToast used outside ToastProvider');
  return api;
}
